<script setup>
import { onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import Card from '@/components/ui/Card.vue'
import Alert from '@/components/ui/Alert.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { t } = useI18n() 
const errorKey = ref(null)
const errorPayload = ref(null)
const isLoading = ref(true)
const errorMessage = computed(() => {
  if (!errorKey.value) return null
  return t(errorKey.value, errorPayload.value)
})

onMounted(async () => {
  const code = route.query.code
  if (!code) {
    errorKey.value = 'authCallback.noCode'
    isLoading.value = false
    setTimeout(() => router.push('/login'), 3000)
    return
  }

  // Get provider and origin from state query parameter (passed through OAuth flow)
  let provider = 'google'
  let targetOrigin = window.location.origin
  const state = route.query.state
  if (state && typeof state === 'string') {
    const providerMatch = state.match(/provider=(\w+)/)
    if (providerMatch) {
      provider = providerMatch[1]
    }
    const originMatch = state.match(/origin=([^&]+)/)
    if (originMatch) {
      targetOrigin = decodeURIComponent(originMatch[1])
    }
  }
  console.log('AuthCallback using provider:', provider, 'targetOrigin:', targetOrigin)

  try {
    const res = await fetch(`/api/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, provider }),
      credentials: 'include'
    })

    if (!res.ok) {
      const errData = await res.json()
      
      if (errData.detail && typeof errData.detail === 'string' && errData.detail.includes('Google')) {
        errorKey.value = 'authCallback.googleError'
        errorPayload.value = { detail: errData.detail }
      } else {
        errorKey.value = 'authCallback.tokenFail'
      }
      
      isLoading.value = false
      setTimeout(() => router.push('/login'), 5000)
      return 
    }
    const userData = await res.json()
    store.commit('setUser', userData)
    store.commit('setAuthCheckComplete', true)

    const pendingActionString = sessionStorage.getItem('pendingUnlockAction')
    if (pendingActionString) {
      try {
        const pendingAction = JSON.parse(pendingActionString)
        // Ne brisemo pendingUnlockAction - UnlockPassword ce ga procitati i postaviti didExplicitLogin
        router.push(`/unlock/${pendingAction.code}`)
      } catch {
        sessionStorage.removeItem('pendingUnlockAction')
        router.push('/')
      }
    } else {
      router.push('/')
    }
  } catch (e) {
    errorKey.value = 'errors.generic'
    isLoading.value = false
    setTimeout(() => router.push('/login'), 5000)
  }
})
</script>

<template>
  <div class="flex items-center justify-center min-h-[calc(100vh-200px)]">
    <Card class="max-w-md w-full text-center">
      <template #header>
        <h2 class="card-title">{{ $t('authCallback.verifying') }}</h2>
      </template>

      <div v-if="isLoading" class="text-muted-foreground">
        <p>{{ $t('authCallback.wait') }}</p>
        <svg class="animate-spin h-6 w-6 mx-auto mt-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0..." />
        </svg>
      </div>
      <div v-if="errorMessage">
        <h3 class="font-semibold text-destructive mb-2">{{ $t('authCallback.errorTitle') }}</h3>
        <Alert type="error" class="mb-2">{{ errorMessage }}</Alert>
        <p class="text-xs text-muted-foreground">{{ $t('authCallback.redirect') }}</p>
      </div>
    </Card>
  </div>
</template>